import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

export interface FieldWithActionProps {
  /**
   * The value to display in the field
   */
  value: string | null | undefined;

  /**
   * The action button to display (typically an IconButton or UpdateButton)
   */
  action?: React.ReactNode;

  /**
   * Text to show when value is null/undefined
   */
  emptyText?: string;

  /**
   * Maximum width of the field
   */
  maxWidth?: string | number;

  /**
   * Custom styles for the Paper component
   */
  sx?: SxProps<Theme>;

  /**
   * Custom styles for the Typography component
   */
  typographySx?: SxProps<Theme>;

  /**
   * Whether to use monospace font
   */
  monospace?: boolean;

  /**
   * Whether to show the action button on hover only
   */
  showActionOnHover?: boolean;
}

/**
 * A reusable field component with an integrated action button.
 * Provides a clean, consistent design for displaying field values with optional actions.
 * The action button appears on hover (or always if showActionOnHover is false).
 */
export const FieldWithAction: React.FC<FieldWithActionProps> = ({
  value,
  action,
  emptyText = 'Not set',
  maxWidth = '300px',
  sx,
  typographySx,
  monospace = false,
  showActionOnHover = true,
}) => {
  const hasValue = value != null && value !== '';
  const [hovered, setHovered] = React.useState(false);
  const showAction = !showActionOnHover || hovered || !hasValue;

  // Always render action but control visibility
  const actionVisible = showAction;

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: maxWidth ? '100%' : 'auto',
        maxWidth: maxWidth || 'none',
        '&:hover .field-action': {
          opacity: 1,
        },
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '0.75rem 1rem',
          paddingRight: action && actionVisible ? '2.75rem' : '1rem',
          backgroundColor: hasValue
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.02)',
          transition: 'padding-right 0.2s ease-in-out',
          ...sx,
        }}
      >
        {hasValue && typeof value === 'string' && value.includes('🟢') ? (
          <Typography
            variant="body2"
            sx={{
              fontFamily: monospace ? 'monospace' : 'inherit',
              color: 'text.primary',
              fontSize: '0.7rem',
              flex: 1,
              minWidth: 0,
              ...typographySx,
            }}
          >
            {value}
          </Typography>
        ) : (
          <Typography
            variant={hasValue ? 'body1' : 'body2'}
            sx={{
              fontFamily: monospace ? 'monospace' : 'inherit',
              color: hasValue ? 'text.primary' : 'text.secondary',
              fontStyle: hasValue ? 'normal' : 'italic',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
              ...typographySx,
            }}
          >
            {hasValue ? value : emptyText}
          </Typography>
        )}
      </Paper>
      {action && (
        <Box
          className="field-action"
          sx={{
            position: 'absolute',
            right: '0.375rem',
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: actionVisible ? 1 : 0,
            transition: 'opacity 0.15s ease-in-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            pointerEvents: actionVisible ? 'auto' : 'none',
            '& > *': {
              minWidth: 'auto !important',
            },
            '& button': {
              minWidth: 'auto !important',
              padding: '4px !important',
              width: 'auto !important',
            },
            '& .MuiButton-root': {
              minWidth: 'auto !important',
              padding: '4px !important',
              width: 'auto !important',
            },
            '& .MuiButton-startIcon': {
              margin: '0 !important',
            },
          }}
        >
          {action}
        </Box>
      )}
    </Box>
  );
};

